import { z } from 'zod';

/** Product threshold shared by server routing and client presentation. */
export const CASE_WIKI_DEFER_FROM_PIECES = 50;

/**
 * Fenêtre de nuit du Case Wiki, heure de Paris : début inclus, fin exclue.
 * Une seule définition pour le dispatcher, la nuit de la box GPU et le texte
 * montré à l'avocat (`DEDICATED_BOX_NIGHT_WINDOW` côté backend la reprend).
 */
export const CASE_WIKI_NIGHT_WINDOW = {
  startsAtHour: 20,
  endsAtHour: 9,
  timeZone: 'Europe/Paris',
} as const;

/** Server-owned sources that may create a durable nightly dispatch request. */
export const CaseWikiDispatchSourceSchema = z.enum([
  'user_ui',
  'case_discovery',
  'edition',
  'nightly_catchup',
]);
export type CaseWikiDispatchSource = z.infer<typeof CaseWikiDispatchSourceSchema>;

/**
 * Routage LLM décidé par le plan de nuit pour un dossier entier.
 *
 * `box` (défaut) : le cran GPU dédié reste en tête de chaîne, gaté par
 * l'occupation live comme pour tout appel non prioritaire.
 * `serverless` : le cran GPU est retiré de la chaîne pour toutes les phases
 * de ce dossier — il absorbe le débordement quand le travail de la nuit dépasse
 * le budget de la box. La valeur est portée par la demande de dispatch, puis
 * par le payload du pipeline et de chacun de ses enfants.
 */
export const CaseWikiLlmRoutingSchema = z.enum(['box', 'serverless']);
export type CaseWikiLlmRouting = z.infer<typeof CaseWikiLlmRoutingSchema>;

/**
 * The durable request surfaced with a Wiki state response while no pipeline is
 * running. It intentionally does not add `scheduled` to `WikiState`.
 */
export const WikiActiveDispatchSchema = z.object({
  id: z.string().uuid(),
  scheduled_for: z.string().datetime(),
  source: CaseWikiDispatchSourceSchema,
});
export type WikiActiveDispatch = z.infer<typeof WikiActiveDispatchSchema>;

/** Terminal technical dispatch failure, separate from the six Wiki states. */
export const WikiFailedDispatchSchema = z.object({
  id: z.string().uuid(),
  failed_at: z.string().datetime(),
  source: CaseWikiDispatchSourceSchema,
});
export type WikiFailedDispatch = z.infer<typeof WikiFailedDispatchSchema>;

/** Result returned by the single Case Wiki launch boundary. */
export const CaseWikiLaunchResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('launched'),
    jobId: z.string(),
    totalPieces: z.number().int().min(0),
  }),
  z.object({
    status: z.literal('already_running'),
    jobId: z.string(),
    totalPieces: z.number().int().min(0),
  }),
  z.object({
    status: z.literal('scheduled'),
    dispatchRequestId: z.string().uuid(),
    scheduledFor: z.string().datetime(),
    totalPieces: z.number().int().min(0),
  }),
]);
export type CaseWikiLaunchResult = z.infer<typeof CaseWikiLaunchResultSchema>;
